/**
 * @author       julian@zumper.com (Julian Napolitan)
 * @copyright    Copyright (c) 2019, Zumper
 * @description  AutocompleteResults component
 */

import { Box } from '@chakra-ui/react'
import { selectIsMobile, selectIsMounted } from '@zcode-ui/web-zumper-global-state'
import { type AutocompleteApiResult, AutocompleteResult } from '@zcode/bizos'
import React from 'react'
import { useSelector } from 'react-redux'
import invariant from 'tiny-invariant'
import type * as Api from '../../../../shared/api'
import { AutocompleteResult as AutocompleteResultComponent } from '../../../../shared/components/Autocomplete/AutocompleteResults/AutocompleteResult'
import { UseMyLocation } from '../../../../shared/components/Autocomplete/AutocompleteResults/UseMyLocation'
import { selectIsFetching } from '../../../../shared/modules/autocomplete/selectors'
import { useAutocompleteContext } from '../AutocompleteContext'
import { type AutocompleteOrigin, type AutocompleteTheme, MIN_CHARACTER_COUNT } from '../constants'
import { getPreviousSearches } from '../helpers/getPreviousSearches'
import { POPULAR_SEARCHES } from './popularSearches'

export interface AutocompleteResultsProps {
  dispatchMixpanelAutocompleteResultClick?: ({
    result,
    index,
  }: {
    result: AutocompleteApiResult
    index: number
  }) => void
  exclude: Parameters<(typeof Api)['autocompleteResults']['query']>[0]['exclude']
  fillInputOnSelect?: boolean
  isMapListNav: boolean | undefined
  isShortTerm?: boolean
  onClickGetCurrentPosition: () => void
  onSelectAutocompleteResult: (result: AutocompleteApiResult) => void
  origin?: AutocompleteOrigin
  resetForm: () => void
  setFieldValue: (field: 'searchTerm', value: string, shouldValidate?: boolean | undefined) => void
  theme: AutocompleteTheme
}

export const AutocompleteResults = ({
  dispatchMixpanelAutocompleteResultClick,
  exclude,
  fillInputOnSelect,
  isMapListNav,
  isShortTerm,
  onClickGetCurrentPosition,
  onSelectAutocompleteResult,
  origin,
  resetForm,
  setFieldValue,
  theme,
}: AutocompleteResultsProps) => {
  const { predictionResults, searchTerm } = useAutocompleteContext()
  const appIsMounted = useSelector(selectIsMounted)
  const isMobile = useSelector(selectIsMobile)
  const isFetching = useSelector(selectIsFetching)

  const prevSearches = appIsMounted ? getPreviousSearches(exclude) : undefined

  let results: readonly AutocompleteApiResult[]
  const isPredictionResults = !!predictionResults?.length
  const isPopularSearches =
    !predictionResults?.length && !(prevSearches?.length && searchTerm.length < MIN_CHARACTER_COUNT)

  const prevSearchIds = prevSearches?.map((prevSearch) => prevSearch.id)
  if (predictionResults?.length) {
    // check if any of the prediction results are in the prevSearches
    // if so add isPrevSearch: true to the result and sort the results
    // with prevSearches at the top
    if (prevSearches?.length) {
      results = predictionResults
        .map((predictionResult) => {
          if (prevSearchIds?.includes(predictionResult.id)) {
            const result = new AutocompleteResult({
              is_previous_search: true,
              ...predictionResult._data,
            })
            invariant(!result.isDerivedFromEntity && result.type !== 'current_position')
          }
          return predictionResult
        })
        .sort((a, b) => {
          if (a.isPreviousSearch !== b.isPreviousSearch) {
            return a.isPreviousSearch ? -1 : 1
          }
          return 0
        })
    } else {
      results = predictionResults
    }
  } else if (prevSearches?.length && searchTerm.length < MIN_CHARACTER_COUNT) {
    results = prevSearches
  } else {
    results = POPULAR_SEARCHES
  }

  const showUseMyLocation = !isPredictionResults && !fillInputOnSelect
  const showNoResults = searchTerm.length >= MIN_CHARACTER_COUNT && isPopularSearches && !isFetching

  return (
    <>
      {showNoResults ? (
        <Box<'p'>
          as="p"
          borderBottomColor="gray.150"
          borderBottomStyle="solid"
          borderBottomWidth="1px"
          color="gray.950"
          fontSize="md"
          fontWeight="bold"
          lineHeight="20px"
          pb={isMobile ? 8 : 6}
          pt={2}
          px={4}
          mb={isMobile ? 8 : 6}
          textAlign="left"
        >
          Sorry, no results
        </Box>
      ) : null}
      {showUseMyLocation && (
        <UseMyLocation
          index={0}
          key={0}
          onClickGetCurrentPosition={onClickGetCurrentPosition}
          origin={origin}
          theme={theme}
        />
      )}
      <>
        {!isPredictionResults && !!results.length && (
          <Box<'div'>
            color="gray.600"
            fontSize="xs"
            fontWeight="medium"
            letterSpacing="1.68px"
            lineHeight="16px"
            marginBottom={isMapListNav ? 4 : 2}
            paddingX={4}
            pt={4}
            textAlign="left"
            textTransform="uppercase"
          >
            {isPopularSearches ? 'Popular' : 'Recent searches'}
          </Box>
        )}
        {results.map((searchResult, idx) => {
          const isLastPrevSearchItem =
            isPredictionResults &&
            ((idx === results.length - 1 && searchResult.isPreviousSearch) ||
              (searchResult.isPreviousSearch && !results[idx + 1].isPreviousSearch))
          const index = showUseMyLocation
            ? idx + 1
            : isPredictionResults || isShortTerm
              ? idx
              : idx + 1
          return (
            <AutocompleteResultComponent
              dispatchMixpanelAutocompleteResultClick={dispatchMixpanelAutocompleteResultClick}
              fillInputOnSelect={fillInputOnSelect}
              index={index}
              isLastPrevSearchItem={isLastPrevSearchItem}
              isMapListNav={isMapListNav}
              isPrevSearch={!!searchResult.isPreviousSearch}
              key={searchResult.id}
              onSelectAutocompleteResult={onSelectAutocompleteResult}
              origin={origin}
              resetForm={resetForm}
              result={searchResult}
              setFieldValue={setFieldValue}
              theme={theme}
            />
          )
        })}
      </>
    </>
  )
}
